/**
 * The account's tools as the picker lists them: the engine's internals hidden, narrowed by the
 * filters and by the words searched in their names, and grouped by integration with the ones this
 * dataset already expects first. A name is the one a row shows or the tool's own (`orders_get`).
 */

import { useMemo } from 'react';
import { useToolDefinitions } from '@/hooks/use-tool-definitions';
import { matchesAllTerms, searchTerms } from '@/lib/search';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import { matchesToolFilter, type ToolFilter } from './tool-filters';
import { toolKey } from './tool-schema';

/** Used by the engine while it loads a sheet's schema; never a call a reply makes. */
const INTERNAL_TOOLS = new Set(['spreadsheets_describe']);

export const useToolCatalogue = (filter: ToolFilter, query: string, suggestedKeys: ReadonlySet<string>) => {
	const { data: definitions = [], isLoading, isError } = useToolDefinitions();
	const terms = useMemo(() => searchTerms(query), [query]);
	const tools = useMemo(() => definitions.filter((tool) => !INTERNAL_TOOLS.has(tool.tool)), [definitions]);
	const providers = useMemo(() => [...new Set(tools.map((tool) => tool.provider))].sort(), [tools]);
	// Both names are on screen when they match: the row shows the tool's own under its label then.
	const shown = useMemo(
		() =>
			tools.filter(
				(tool) => matchesToolFilter(tool, filter) && matchesAllTerms(`${tool.label} ${tool.tool}`.toLowerCase(), terms)
			),
		[tools, filter, terms]
	);
	const byKey = useMemo(() => new Map(shown.map((tool) => [toolKey(tool.provider, tool.tool), tool])), [shown]);
	const suggested = useMemo(
		() => shown.filter((tool) => suggestedKeys.has(toolKey(tool.provider, tool.tool))),
		[shown, suggestedKeys]
	);
	const groups = useMemo(() => {
		const grouped = new Map<string, ToolDefinitionAction[]>();
		for (const tool of shown) grouped.set(tool.provider, [...(grouped.get(tool.provider) ?? []), tool]);
		return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));
	}, [shown]);

	return { isLoading, isError, terms, providers, byKey, suggested, groups };
};

export type ToolCatalogue = ReturnType<typeof useToolCatalogue>;
